import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, CircularProgress } from "@mui/material";
import type { ReactNode } from "react";
import { IconChip } from "@/pages/sawlines/components/primitives";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import type {
	MillBundle,
	MillBundleState,
} from "../repositories/tagStationRepository";
import {
	fmtLbs,
	fmtLen,
	notSent,
	printing,
	type SectionMap,
	SOURCES,
	type Values,
	weightLbs,
} from "../tagStation";
import { Card, labelSx } from "./Card";

export const STATE: Record<MillBundleState, { label: string; color: string }> =
	{
		Station: { label: "At the station", color: "#2e7d32" },
		ToPrint: { label: "Waiting for its tag", color: "#ed6c02" },
		Aside: { label: "Set aside", color: "#8a5a00" },
		Printed: { label: "Printed", color: "#757575" },
	};

export const NOT_SENT = { label: "Tag not sent", color: "#c62828" };

export const notSentWhy = (b: MillBundle) =>
	`${NOT_SENT.label}. ${b.PrintMessage ?? "It did not reach the printer."} Reprint it.`;

// takes the icon props it ignores, to sit where an icon goes
const Spinner = (_: { sx?: object }) => (
	<CircularProgress size={12} thickness={6} color="inherit" />
);

// the chip for a printed bundle tells how its last print is doing
export const printState = (b: MillBundle) =>
	notSent(b)
		? { ...NOT_SENT, Icon: WarningAmberIcon, title: notSentWhy(b) }
		: printing(b)
			? {
					label: "Printing…",
					color: "#1565C0",
					Icon: Spinner,
					title: b.PrintMessage
						? `Waiting for the printer. ${b.PrintMessage}`
						: "Sending it to the printer.",
				}
			: null;

export const fromText = (b: MillBundle) =>
	`${b.SourceNote ?? SOURCES[b.Source]}${b.IsManual ? " · by hand" : ""}`;

export const materialText = (v: Values) =>
	`${v.section} · Gr ${v.grade || "—"} · ${fmtLen(v.lengthIn)}`;

export const whenText = (b: MillBundle) =>
	b.PrintedAt
		? `${dxtToLocalServerTime(b.PrintedAt, "HH:mm")} · #${b.PrintedByEmployee ?? "—"}`
		: dxtToLocalServerTime(b.ArrivedAt, "HH:mm");

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
	<Box
		sx={{
			flex: "0 0 34px",
			"@media (max-height:960px)": { flexBasis: 30 },
			display: "flex",
			alignItems: "center",
			gap: 1.5,
			borderBottom: "1px solid #f1f1f1",
			"&:last-child": { borderBottom: 0 },
		}}
	>
		<Box sx={{ ...labelSx, whiteSpace: "nowrap" }}>{label}</Box>
		<Box
			title={typeof children === "string" ? children : undefined}
			sx={{
				ml: "auto",
				minWidth: 0,
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				fontSize: "1.1rem",
				fontWeight: 600,
				fontVariantNumeric: "tabular-nums",
			}}
		>
			{children}
		</Box>
	</Box>
);

export const BundleCard = ({
	bundle,
	values,
	sections,
	onAside,
	onScrap,
}: {
	bundle: MillBundle;
	values: Values;
	sections: SectionMap;
	onAside: () => void;
	onScrap: () => void;
}) => {
	const print = printState(bundle);
	const state = print ?? STATE[bundle.State];
	const printed = bundle.State === "Printed";
	return (
		<Card title="Bundle">
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					gap: 1.25,
					px: 1.75,
					pt: 1.25,
					pb: 1.5,
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						pl: 1.25,
						borderLeft: `3px solid ${state.color}`,
					}}
				>
					<Row label="Material">{materialText(values)}</Row>
					<Row label="Status">
						<Box sx={{ display: "flex", gap: 0.75 }}>
							<IconChip
								label={state.label}
								color={state.color}
								title={print?.title}
								filled={bundle.State === "Station"}
								Icon={
									print?.Icon ??
									(bundle.State === "Aside" ? AccessTimeIcon : undefined)
								}
							/>
							{bundle.IsScrap && (
								<IconChip label="Scrap" color="#c62828" Icon={BlockIcon} />
							)}
						</Box>
					</Row>
				</Box>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Row label="Pieces">{values.pieces}</Row>
					<Row label="Heat">{values.heat || "—"}</Row>
					<Row label="Weight">{fmtLbs(weightLbs(values, sections))}</Row>
					<Row label="From">{fromText(bundle)}</Row>
					<Row label={printed ? "Printed" : "Arrived"}>{whenText(bundle)}</Row>
					<Row label="Tag">
						{bundle.TagNo ? (
							shownTagNo(bundle.TagNo)
						) : (
							<Box component="span" sx={{ color: "text.secondary" }}>
								At print
							</Box>
						)}
					</Row>
				</Box>
				{/* height kept without buttons: the rows never move */}
				<Box
					sx={{
						mt: "auto",
						flexShrink: 0,
						minHeight: 36.5,
						display: "flex",
						gap: 1,
						"& > button": { flex: 1, px: 0.75, whiteSpace: "nowrap" },
					}}
				>
					{!printed && (
						<>
							<Button
								variant="outlined"
								color="warning"
								startIcon={<AccessTimeIcon />}
								onClick={onAside}
								sx={{ color: "#8a5a00", borderColor: "#e0a100" }}
							>
								{bundle.State === "Aside" ? "Back on transfer" : "Set aside"}
							</Button>
							<Button
								variant="outlined"
								color="error"
								startIcon={<BlockIcon />}
								onClick={onScrap}
							>
								{bundle.IsScrap ? "Not scrap" : "Mark as scrap"}
							</Button>
						</>
					)}
				</Box>
			</Box>
		</Card>
	);
};
